import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getDevicesTotalCount } from './devices-multiselect-api'
import { useDispatch, useSelector } from 'react-redux'
import { enqueueSnackbar } from '../../actions'

export const useDevicesMultiselect = (filters, locationId = null) => {
    const dispatch = useDispatch()
    const locale = useSelector(state => state.main.locale)

    const [selectedRows, setSelectedRows] = useState({})
    const [excludedRows, setExcludedRows] = useState({})
    const [isAllSelectorOn, setIsAllSelectorOn] = useState(false)
    const [
        isMovingToLocationModalOpen,
        setIsMovingToLocationModalOpen
    ] = useState(false)
    const [isDeleteDevicesModalOpen, setIsDeleteDevicesModalOpen] = useState(
        false
    )
    const [isSwitchOffModalOpen, setIsSwitchOffModalOpen] = useState(false)
    const [isMModeDevicesModalOpen, setIsMModeDevicesModalOpen] = useState(
        false
    )
    const [selectedRowsAmount, setSelectedRowsAmount] = useState(0)

    const isRowSelected = useCallback(
        deviceId => {
            if (isAllSelectorOn) {
                return !excludedRows.hasOwnProperty(deviceId)
            } else {
                return selectedRows.hasOwnProperty(deviceId)
            }
        },
        [selectedRows, isAllSelectorOn, excludedRows]
    )

    const selectRow = useCallback(
        (deviceId, selected) => {
            if (isAllSelectorOn) {
                const newExcludedRows = { ...excludedRows }
                if (selected) {
                    delete newExcludedRows[deviceId]
                } else {
                    newExcludedRows[deviceId] = true
                }
                setExcludedRows(newExcludedRows)
            } else {
                const newSelectedRows = { ...selectedRows }
                if (selected) {
                    newSelectedRows[deviceId] = true
                } else {
                    delete newSelectedRows[deviceId]
                }
                setSelectedRows(newSelectedRows)
            }
        },
        [selectedRows, excludedRows, isAllSelectorOn]
    )

    const toggleAllSelector = useCallback(() => {
        if (isAllSelectorOn && Object.keys(excludedRows).length > 0) {
            setExcludedRows({})
            setSelectedRows({})
        } else {
            setExcludedRows({})
            setSelectedRows({})
            setIsAllSelectorOn(!isAllSelectorOn)
        }
    }, [isAllSelectorOn, excludedRows])

    const selectedDevicesIds = useMemo(() => {
        return Object.keys(selectedRows)
    }, [selectedRows])

    const excludedDevicesIds = useMemo(() => {
        return Object.keys(excludedRows)
    }, [excludedRows])

    const isAllCheckboxIndeterminate = useMemo(() => {
        return isAllSelectorOn && Object.keys(excludedRows).length > 0
    }, [isAllSelectorOn, excludedRows])

    const clearSelection = () => {
        // must be called before setSelectedRows({}) and setExcludedRows({})
        // because useEffect is triggered after setSelectedRows({}) and setExcludedRows({})
        setIsAllSelectorOn(false)
        setSelectedRows({})
        setExcludedRows({})
    }

    const getDevicesTotal = useCallback(async () => {
        const requestFilters = { ...filters }
        if (locationId && locationId !== 'new') {
            requestFilters.locationId = locationId
            requestFilters.with_children = 1
        }
        const result = await getDevicesTotalCount(requestFilters)
        return Number(result.total)
    }, [filters, locationId])

    useEffect(() => {
        if (!isAllSelectorOn) {
            setSelectedRowsAmount(Object.keys(selectedRows).length)
        } else {
            getDevicesTotal()
                .then(dTotal => {
                    setSelectedRowsAmount(
                        dTotal - Object.keys(excludedRows).length
                    )
                })
                .catch(() => {
                    dispatch(
                        enqueueSnackbar({
                            message: locale.sagasMsgs.commonLoadingError,
                            options: {
                                variant: 'error'
                            }
                        })
                    )
                })
        }
    }, [selectedRows, isAllSelectorOn, excludedRows])

    return {
        selectedRows,
        isRowSelected,
        selectRow,
        selectedRowsAmount,
        clearSelection,
        isMovingToLocationModalOpen,
        setIsMovingToLocationModalOpen,
        isDeleteDevicesModalOpen,
        setIsDeleteDevicesModalOpen,
        selectedDevicesIds,
        excludedDevicesIds,
        toggleAllSelector,
        isAllSelectorOn,
        isAllCheckboxIndeterminate,
        isSwitchOffModalOpen,
        setIsSwitchOffModalOpen,
        isMModeDevicesModalOpen,
        setIsMModeDevicesModalOpen
    }
}
